import { defineStore } from 'pinia'
import { ref, computed } from 'vue'

const API_BASE_URL = 'http://localhost:8000'

export const useAuthStore = defineStore('auth', () => {
  // State
  const user = ref(null)
  const token = ref(localStorage.getItem('auth_token') || null)
  const loading = ref(false)

  // Getters
  const isAuthenticated = computed(() => !!token.value && !!user.value)
  const isLoggedIn = computed(() => isAuthenticated.value)

  // Actions
  const setToken = (newToken) => {
    token.value = newToken
    if (newToken) {
      localStorage.setItem('auth_token', newToken)
    } else {
      localStorage.removeItem('auth_token')
    }
  }

  const setUser = (userData) => {
    user.value = userData
  }

  const clearAuth = () => {
    user.value = null
    setToken(null)
  }

  const apiRequest = async (endpoint, options = {}) => {
    const url = `${API_BASE_URL}${endpoint}`
    const config = {
      headers: {
        'Content-Type': 'application/json',
        ...options.headers
      },
      ...options
    }

    // Add auth header if token exists
    if (token.value) {
      config.headers.Authorization = `Bearer ${token.value}`
    }

    const response = await fetch(url, config)
    
    if (!response.ok) {
      const errorData = await response.json().catch(() => ({}))
      throw new Error(errorData.detail || `HTTP error! status: ${response.status}`)
    }

    return response.json()
  }

  const register = async (userData) => {
    loading.value = true
    try {
      const response = await apiRequest('/auth/register', {
        method: 'POST',
        body: JSON.stringify(userData)
      })
      
      // After successful registration, automatically log in
      await login(userData.email, userData.password)
      
      return response
    } finally {
      loading.value = false
    }
  }

  const login = async (email, password) => {
    loading.value = true
    try {
      const response = await apiRequest('/auth/login', {
        method: 'POST',
        body: JSON.stringify({ email, password })
      })
      
      setToken(response.access_token)
      await fetchCurrentUser()
      
      return response
    } finally {
      loading.value = false
    }
  }

  const logout = async () => {
    try {
      await apiRequest('/auth/logout', { method: 'POST' })
    } catch (error) {
      console.warn('Logout API call failed:', error)
    } finally {
      clearAuth()
    }
  }

  const fetchCurrentUser = async () => {
    if (!token.value) return null
    
    try {
      const userData = await apiRequest('/auth/me')
      setUser(userData)
      return userData
    } catch (error) {
      console.error('Failed to fetch current user:', error)
      // If token is invalid, clear auth
      if (error.message.includes('401')) {
        clearAuth()
      }
      return null
    }
  }

  const initializeAuth = async () => {
    if (token.value) {
      await fetchCurrentUser()
    }
  }

  return {
    // State
    user,
    token,
    loading,
    
    // Getters
    isAuthenticated,
    isLoggedIn,
    
    // Actions
    register,
    login,
    logout,
    fetchCurrentUser,
    initializeAuth,
    setToken,
    setUser,
    clearAuth
  }
}) 